import Container from '@risk-smart/themed-cloudscape-components/container';
import Header from '@risk-smart/themed-cloudscape-components/header';
import type { ParentIssueType } from '@risksmart-app/domain/src/types/consts';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { PageLayout } from 'src/layouts';
import type { IssueFormDataFields } from 'src/pages/issues/update/forms/issueSchema';

import { useReportPublicIssue } from '@/hooks/mutations';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { createWithFiles } from '@/utils/createWithFiles';
import { IssueTypeMapping } from '@/utils/issueVariantUtils';

import IssueForm from '../update/forms/IssueForm';
import IssueTypeSelector from './IssueTypeSelector';

const Page: FC = () => {
  const { updateFiles } = useFileUpdate();
  const [issueType, setIssueType] = useState<ParentIssueType>(
    Parent_Type_Enum.Issue
  );
  const issueMapping = IssueTypeMapping[issueType];
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: issueMapping.taxonomy,
  });
  const goldenCharterIssuesEnabled = useIsFeatureFlagEnabled('issue-gc');
  const allicaIssuesEnabled = useIsFeatureFlagEnabled('issue-allica');
  const additionalIssueVariants =
    goldenCharterIssuesEnabled || allicaIssuesEnabled;
  const defaultTitle = st('report_issue_title');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');

  const { reportPublicIssue } = useReportPublicIssue();

  const onSave = async (data: IssueFormDataFields) => {
    const { files } = data;
    const { createdEntity } = await createWithFiles({
      create: () =>
        reportPublicIssue({
          Type: issueType,
          CustomAttributeData: data.CustomAttributeData || undefined,
          TagTypeIds: data.tags?.map((t) => t.TagTypeId) || [],
          DepartmentTypeIds:
            data.departments?.map((d) => d.DepartmentTypeId) || [],
          DateIdentified: data.DateIdentified,
          DateOccurred: data.DateOccurred,
          Details: data.Details,
          Title: data.Title,
          ...ownerContributorAndViewerIds(data),
          IsExternalIssue: data.IsExternalIssue,
          ImpactsCustomer: data.ImpactsCustomer,
        }),
      getCreatedEntity: (createResult) => {
        const parentId = createResult.insertChildIssue?.Id;
        const sequentialId = createResult.insertChildIssue?.SequentialId;
        if (!parentId || !sequentialId) {
          throw new Error('Missing data after issue insertion');
        }

        return {
          id: parentId,
          // Public reports are intentionally ownerless, so the reporter cannot
          // read the authenticated details page. Partial file failure must land on
          // the same acknowledgement route as a fully successful submission.
          destination: issueMapping.reportedSuccessfullyUrl(
            sequentialId.toString()
          ),
        };
      },
      saveFiles: ({ id }) =>
        updateFiles({
          parentType: Parent_Type_Enum.Issue,
          parentId: id,
          originalFiles: [],
          selectedFiles: files,
        }),
    });

    return {
      savedIds: [createdEntity.id],
      createdEntity,
      successNavigation: { destination: createdEntity.destination },
    };
  };

  const beforeIssueSlot = (
    <>
      <div className={'pb-5'}>
        <IssueTypeSelector
          testId={'IssueType'}
          value={issueType}
          readOnly={false}
          onChange={(val) => {
            setIssueType(val);
          }}
        />
      </div>
    </>
  );

  return (
    <PageLayout
      title={defaultTitle}
      // Object-level toolbar (CUS-156) — flag-gated, title-only. The report form
      // keeps its own Create / Cancel controls; the toolbar just replaces the
      // page header. Legacy header is the flag-off fallback.
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={{ header: { title: defaultTitle } }} />
        ) : undefined
      }
      actions={<></>}
    >
      <Container header={<Header variant={'h2'}>{t('details')}</Header>}>
        <IssueForm
          onSave={onSave}
          issueType={issueType}
          renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
          beforeFieldsSlot={additionalIssueVariants ? beforeIssueSlot : <></>}
        />
      </Container>
    </PageLayout>
  );
};

export default Page;
